/**
 * MoneyInput — a controlled amount field that edits in MAJOR units (dinars) but reports integer
 * CENTIMES to the parent. Mono + LTR-island so the figure reads identically under RTL. The raw text
 * is kept locally so a user can type "1234," mid-entry without the value jumping.
 */
import { useEffect, useState } from "react";
import { Input } from "@/ui";
import { CURRENCY, centimesToEditable, parseToCentimes } from "../money";

export interface MoneyInputProps {
  /** Current value in centimes, or null when empty/invalid. */
  valueCentimes: number | null;
  /** Called with the parsed centimes (or null when the field is empty/invalid). */
  onChangeCentimes: (centimes: number | null) => void;
  id?: string | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  "aria-describedby"?: string | undefined;
  "aria-invalid"?: boolean | undefined;
  "aria-required"?: boolean | undefined;
  "aria-label"?: string | undefined;
  autoFocus?: boolean | undefined;
}

export function MoneyInput({
  valueCentimes,
  onChangeCentimes,
  id,
  invalid,
  disabled,
  placeholder = "0,00",
  autoFocus,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  "aria-label": ariaLabel,
}: MoneyInputProps) {
  const [text, setText] = useState<string>(() => centimesToEditable(valueCentimes));

  // Re-sync when the parent resets the value (e.g. after a successful submit) without clobbering
  // an in-progress edit that already parses to the same centimes.
  useEffect(() => {
    if (parseToCentimes(text) !== valueCentimes) {
      setText(centimesToEditable(valueCentimes));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync only on external value change.
  }, [valueCentimes]);

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nv-s-2)", width: "100%" }}>
      <Input
        mono
        className="nv-ltr-island"
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        style={{ textAlign: "end" }}
        invalid={!!invalid}
        disabled={!!disabled}
        autoFocus={!!autoFocus}
        {...(id ? { id } : {})}
        {...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {})}
        {...(ariaInvalid !== undefined ? { "aria-invalid": ariaInvalid } : {})}
        {...(ariaRequired !== undefined ? { "aria-required": ariaRequired } : {})}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          onChangeCentimes(parseToCentimes(next));
        }}
      />
      <span aria-hidden style={{ color: "var(--nv-slate)", fontSize: "var(--nv-fs-label)" }}>
        {CURRENCY}
      </span>
    </span>
  );
}
